import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { RSVP, Event } from '@/types';
import { Ticket } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import {
  validateMultiTenantTicketSelections,
  mergeMultiTenantTicketsIntoResponses,
  computeMultiTenantCartTotal,
} from '@/lib/rsvp-multi-tenant-ticket';

export default function EditMultiTenantTicketsPage() {
  const { unitId, eventId, rsvpId } = useParams<{ unitId: string; eventId: string; rsvpId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rsvp, setRsvp] = useState<RSVP | null>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchData = async () => {
      if (!unitId || !eventId || !rsvpId) {
        showError('Invalid link');
        setLoading(false);
        return;
      }

      try {
        const rsvpDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId));
        if (!rsvpDoc.exists()) {
          showError('RSVP not found');
          setLoading(false);
          return;
        }

        const rsvpData = rsvpDoc.data() as RSVP;

        if (rsvpData.userId !== currentUser?.uid) {
          showError('You are not authorized to edit this booking');
          navigate('/dashboard');
          return;
        }

        const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
        if (!eventDoc.exists()) {
          showError('Event not found');
          setLoading(false);
          return;
        }

        const eventData = eventDoc.data();
        const eventDate = eventData.date?.toDate() || new Date();
        const eventObj: Event = {
          id: eventDoc.id,
          unitId,
          name: eventData.name,
          date: eventDate,
          location: eventData.location,
          questionnaire: eventData.questionnaire || [],
          courses: eventData.courses || [],
          tickets: eventData.tickets || [],
          ticketMode: eventData.ticketMode === 'multiTenant' ? 'multiTenant' : 'mealChoice',
          maxCapacity: eventData.maxCapacity,
          currentAttendees: eventData.currentAttendees || 0,
          waitlistEnabled: eventData.waitlistEnabled || false,
          waitlist: eventData.waitlist || [],
          rsvpDeadline: eventData.rsvpDeadline?.toDate() || new Date(),
          paymentDeadline: eventData.paymentDeadline?.toDate() || eventDate,
          eventStatus: eventData.eventStatus,
          dresscode: eventData.dresscode,
          customInstructions: eventData.customInstructions,
          isPublic: eventData.isPublic ?? false,
          requirePrefixRankUnitInfo: eventData.requirePrefixRankUnitInfo ?? true,
          createdBy: eventData.createdBy,
          createdAt: eventData.createdAt?.toDate() || new Date(),
        };

        if (new Date() > eventObj.rsvpDeadline) {
          showError('The RSVP deadline has passed. You can no longer edit your tickets.');
          setLoading(false);
          return;
        }

        setEvent(eventObj);
        setRsvp(rsvpData);

        const initialQuantities: Record<string, number> = {};
        (rsvpData.responses?.Tickets || []).forEach((t: any) => {
          initialQuantities[t.id] = t.qty || 0;
        });
        setQuantities(initialQuantities);
      } catch (error) {
        console.error('Error fetching data:', error);
        showError('Failed to load your booking');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [unitId, eventId, rsvpId, currentUser, navigate]);

  const setQuantity = (ticketId: string, qty: number) => {
    setQuantities((prev) => ({ ...prev, [ticketId]: Math.max(0, qty) }));
  };

  const availableTickets = event?.tickets || [];
  const cartTotal = computeMultiTenantCartTotal(availableTickets, quantities);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!event || !unitId || !eventId || !rsvpId || !rsvp) {
      showError('Invalid booking');
      return;
    }

    const validationError = validateMultiTenantTicketSelections(availableTickets, quantities);
    if (validationError) {
      showError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const responses: Record<string, any> = { ...(rsvp.responses || {}) };
      mergeMultiTenantTicketsIntoResponses(responses, availableTickets, quantities);

      await updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId), {
        responses,
        updatedAt: new Date(),
      });

      showSuccess('Ticket quantities updated successfully!');
      navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/tickets-dashboard`);
    } catch (error) {
      console.error('Error updating tickets:', error);
      showError('Failed to update your tickets. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading your booking...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event || !rsvp) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Booking Not Found</CardTitle>
              <CardDescription>This booking doesn't exist or you don't have permission to edit it.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-3xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/tickets-dashboard`)}>
              ← Back to My Tickets
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                Edit Ticket Quantities
              </CardTitle>
              <CardDescription>Update how many of each ticket type you'd like for {event.name}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  {availableTickets.map((t) => (
                    <div key={t.id} className="rounded-lg border p-4">
                      <div className="grid gap-4 md:grid-cols-3 items-end">
                        <div className="space-y-2">
                          <Label>Ticket</Label>
                          <div className="rounded-md border px-3 py-2 text-sm">{t.name}</div>
                        </div>
                        <div className="space-y-2">
                          <Label>Price</Label>
                          <div className="rounded-md border px-3 py-2 text-sm">{formatCurrency(t.price || 0)}</div>
                        </div>
                        <div className="space-y-2">
                          <Label>Quantity</Label>
                          <Input
                            type="number"
                            min={0}
                            max={t.totalAvailable ?? undefined}
                            value={quantities[t.id] ?? 0}
                            onChange={(e) => setQuantity(t.id, parseInt(e.target.value, 10) || 0)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between border-t pt-4">
                  <span className="font-semibold">New Total</span>
                  <span className="text-xl font-bold">{formatCurrency(cartTotal)}</span>
                </div>

                <div className="flex justify-end space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/tickets-dashboard`)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Updating...' : 'Update Tickets'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
